import "./form-waiver.scss";
import { useFormContext } from "react-hook-form";
import { WaiverFormSchema } from "@factory/form/form.rule";
import InputDigitControl from "@shared/ui/hook-form/input-digit-control/input-digit-control";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import CheckboxControl from "@shared/ui/hook-form/checkbox-control/checkbox-control";
import InputTextareaControl from "@shared/ui/hook-form/input-textarea-control/input-textarea-control";
import { Button } from "primereact/button";
import { SyntheticEvent } from "react";
import { GetWaiverDto } from "@api/api";
import { WAIVER_CHECKBOX_TOKEN } from "@shared/utility/waiver-checkbox";

type InputFormSchema = WaiverFormSchema;

export interface FormWaiverProps {
  handleWaiverPreview: () => void;
}
export function FormWaiver({ handleWaiverPreview }: FormWaiverProps) {
  const { control, setValue, getValues } = useFormContext<WaiverFormSchema>();
  // Inserts a checkbox token at the cursor (or at the end if the textarea
  // hasn't been focused) so authors don't need to know the syntax.
  const handleInsertCheckbox = () => {
    const description = getValues("description") ?? "";
    const textarea = document.getElementById(
      "description"
    ) as HTMLTextAreaElement | null;
    const start = textarea?.selectionStart ?? description.length;
    const end = textarea?.selectionEnd ?? description.length;
    const token = `${WAIVER_CHECKBOX_TOKEN} `;

    setValue(
      "description",
      description.slice(0, start) + token + description.slice(end)
    );

    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  const handleDefaultWaiver = (e: SyntheticEvent) => {
    const defaultWaiver = `<center>MEDICAL SERVICE WAIVER</center>

I, <b>[patient]</b>, acknowledge that I have voluntarily elected to receive <b>[service]</b> from <b>[doctor]</b>. 

I understand that this service consists of <b>[session]</b> sessions and is being provided for my benefit.

I understand and acknowledge the following: Nature of Service: I have been informed about the purpose, benefits, and potential risks of <b>[service]</b>.

Voluntary Participation: My decision to undergo this service is entirely voluntary, and I may discontinue treatment at any time.

No Guarantee of Results: I acknowledge that no guarantees have been made regarding the success or effectiveness of the treatment.

Assumption of Risk: I accept all risks associated with this service and agree not to hold [doctor], their staff, or the medical facility liable for any adverse effects.

Informed Consent: I confirm that I have had the opportunity to ask questions and receive answers regarding the service and that I understand all the information provided.

By signing below, I acknowledge that I have read, understood, and agree to this waiver.`;
    setValue("description", defaultWaiver);
  };
  return (
    <div id="FormWaiver" className="waiver">
      <InputControl<InputFormSchema>
        control={control}
        name="name"
        label="Name"
        className="w-full md:w-3/4"
        containerClassName="pb-2"
        placeholder="Enter name"
      />
      <InputControl<InputFormSchema>
        control={control}
        name="subtitle"
        label="Subtitle"
        className="w-full md:w-3/4"
        containerClassName="pb-2"
        placeholder="Enter subtitle"
      />
      <InputTextareaControl<InputFormSchema>
        control={control}
        name="description"
        label="Description"
        className="w-full md:w-3/4"
        containerClassName="pb-2"
        placeholder="Enter description"
        hint='Type "[ ]" to add a checkbox (or "[x]" for one checked by default). Staff can tick them when creating a contract.'
      />
      <div className="flex gap-4 mb-8">
        <Button
          outlined
          className="block h-12 w-48 mt-3"
          label="Use Default Waiver"
          severity="secondary"
          onClick={handleDefaultWaiver}
        />
        <Button
          outlined
          className="block h-12 w-44 mt-3"
          label="Insert Checkbox"
          icon="pi pi-check-square"
          severity="secondary"
          onClick={handleInsertCheckbox}
        />
        <Button
          className="block h-12 w-32 mt-3"
          label="Preview"
          severity="secondary"
          outlined
          onClick={handleWaiverPreview}
        />
      </div>
      <InputDigitControl<InputFormSchema>
        control={control}
        name="fontSize"
        label="Font Size"
        className="w-full md:w-3/4"
        containerClassName="pb-2 mt-12"
        placeholder="Enter fontSize"
      />
    </div>
  );
}

export default FormWaiver;
